import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import "./BlogPage.css";
import back from "../../img/icons/BeigeArrowPrev.png";
import forward from "../../img/icons/BeigeArrowNext.png";
import blogShareIcon from "../../img/icons/blog_share_your_essay.png";
import ApiService from "../../services/ApiService";
import NotificationService from "../../components/NotificationService/NotificationService";
import ValueTiles from "../../components/ValueTiles/ValueTiles";
import useLike from "../../hooks/useLike";

// Category colors matching static HTML
const categoryColors = {
  default: '#C4A5A0',
  kindness: '#C4A5A0',
  curiosity: '#C5CBA3',
  respect: '#B8A898',
  awareness: '#D8C8E8',
  empowerment: '#C4A5A0',
  community: '#C5CBA3'
};

// Dance style options
const DANCE_STYLES = [
  'Salsa', 'Bachata', 'Kizomba', 'Zouk', 'West Coast Swing',
  'Tango', 'Ballroom', 'Latin', 'Hip Hop', 'Contemporary'
];

// Topic options (matching the values)
const TOPIC_OPTIONS = [
  { label: 'Kindness', value: 'kindness' },
  { label: 'Curiosity', value: 'curiosity' },
  { label: 'Respect', value: 'respect' },
  { label: 'Awareness', value: 'awareness' },
  { label: 'Empowerment', value: 'empowerment' },
  { label: 'Community', value: 'community' },
];

// Combined filter options
const ALL_FILTER_OPTIONS = [...DANCE_STYLES, ...TOPIC_OPTIONS.map(t => t.label)];

const MAX_TAGS = 7;

const BlogPage = () => {
  const { articleId } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedBlogId, setExpandedBlogId] = useState(articleId || null);
  const [sortBy, setSortBy] = useState('liked');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const blogsPerPage = 6;

  // Filter states
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [tagSearch, setTagSearch] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);

  // Category filter states
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);

  // Track which articles have been viewed this session (avoid double-counting)
  const viewedArticlesRef = useRef(new Set());

  // Check if user is logged in
  const isLoggedIn = useCallback(() => {
    return localStorage.getItem('authToken') || localStorage.getItem('token');
  }, []);

  // Use the reusable like hook
  const {
    toggleLike,
    isLiked,
    getLikeCount,
    initializeBatch,
    updateLikeCount,
    setLiked,
  } = useLike({
    entityType: 'article',
    isLoggedIn,
    onAuthRequired: () => setShowLoginModal(true),
    onError: (msg) => NotificationService.notifyError(msg),
  });

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await ApiService.get("getAllTitles");
        const titles = response?.data?.data?.titles || response?.data?.titles || [];
        setCategories(titles);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await ApiService.get("getApprovedBlogs");
        const responseData = response?.data;

        let fetchedPosts = [];
        if (responseData?.data?.blogs && Array.isArray(responseData.data.blogs)) {
          fetchedPosts = responseData.data.blogs;
        } else if (responseData?.data && Array.isArray(responseData.data)) {
          fetchedPosts = responseData.data;
        } else if (responseData?.blogs && Array.isArray(responseData.blogs)) {
          fetchedPosts = responseData.blogs;
        } else if (Array.isArray(responseData)) {
          fetchedPosts = responseData;
        }

        setPosts(Array.isArray(fetchedPosts) ? fetchedPosts : []);
      } catch (error) {
        console.error("Error fetching blogs:", error);
        setError("Failed to load blogs. Please try again later.");
        NotificationService.notifyError("Failed to load blogs");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Initialize like counts from posts and fetch user's liked articles if logged in
  useEffect(() => {
    if (posts.length === 0) return;

    // Initialize like counts for all posts
    initializeBatch(
      posts,
      () => false, // Initial liked state (will be updated below if logged in)
      (post) => post.likeCount || 0
    );

    // Fetch which articles user has liked
    const fetchUserLikes = async () => {
      if (!isLoggedIn()) return;

      try {
        const blogIds = posts.map(post => post._id);
        const response = await ApiService.post('blogs/check-likes', { blogIds });

        // Backend returns: { success: true, data: { blogId: { liked: true/false, likeCount: N } } }
        const likeStatus = response?.data?.data || {};

        // Parse the object response and set liked state for each article
        Object.entries(likeStatus).forEach(([blogId, status]) => {
          if (status && status.liked) {
            setLiked(blogId, true);
          }
        });
      } catch (error) {
        console.error("Error fetching user likes:", error);
      }
    };

    fetchUserLikes();
  }, [posts, isLoggedIn, initializeBatch, setLiked]);

  // Handle direct article link from URL
  useEffect(() => {
    if (!articleId || posts.length === 0) return;

    // Find the index of the article in the posts array
    const articleIndex = posts.findIndex(post => post._id === articleId);
    if (articleIndex === -1) return;

    // Calculate which page the article is on
    const pageNumber = Math.floor(articleIndex / blogsPerPage) + 1;
    setCurrentPage(pageNumber);
    setExpandedBlogId(articleId);

    // Scroll to the article after a short delay
    setTimeout(() => {
      const expandedEl = document.getElementById(`expanded-${articleId}`);
      if (expandedEl) {
        expandedEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  }, [articleId, posts, blogsPerPage]);

  // Filter and sort posts
  const filteredAndSortedPosts = useMemo(() => {
    let filtered = [...posts];

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(post => post.title?._id === selectedCategory);
    }

    // Filter by selected styles (includes dance styles, topics, and article title search)
    if (selectedStyles.length > 0) {
      filtered = filtered.filter(post => {
        const postStyles = post.danceStyles || [];
        const postTopic = post.title?.name || '';
        const articleTitle = (post.articleTitle || '').toLowerCase();
        const postContent = (post.message || '').toLowerCase();
        const postHashtags = post.hashtags || [];

        return selectedStyles.some(style => {
          const styleLower = style.toLowerCase();

          // Check dance styles
          const matchesDanceStyle = postStyles.some(ps =>
            ps.toLowerCase().includes(styleLower)
          );
          // Check topic/category
          const matchesTopic = postTopic.toLowerCase() === styleLower;
          // Check article title (partial match)
          const matchesTitle = articleTitle.includes(styleLower);
          // Check hashtags
          const matchesHashtag = postHashtags.some(tag =>
            tag.toLowerCase().includes(styleLower)
          );

          return matchesDanceStyle || matchesTopic || matchesTitle || matchesHashtag;
        });
      });
    }

    // Sort
    switch (sortBy) {
      case 'liked':
        filtered.sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0));
        break;
      case 'viewed':
        filtered.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
        break;
      case 'recent':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        break;
    }

    return filtered;
  }, [posts, selectedStyles, selectedCategory, sortBy]);

  // Check if we can add more tags
  const canAddMoreTags = selectedStyles.length < MAX_TAGS;

  // Filter suggestions based on search (combines dance styles and topics)
  const tagSuggestions = useMemo(() => {
    const available = ALL_FILTER_OPTIONS.filter(tag => !selectedStyles.includes(tag));
    if (!tagSearch.trim()) return available;
    const searchLower = tagSearch.toLowerCase();
    return available.filter(tag => tag.toLowerCase().includes(searchLower));
  }, [tagSearch, selectedStyles]);

  // Category suggestions filtered by search
  const categorySuggestions = useMemo(() => {
    if (!categorySearch.trim()) return categories;
    const searchLower = categorySearch.toLowerCase();
    return categories.filter(cat => cat.name.toLowerCase().includes(searchLower));
  }, [categorySearch, categories]);

  // Add a tag from suggestions or custom input
  const addTag = (tag) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !selectedStyles.includes(trimmedTag) && canAddMoreTags) {
      setSelectedStyles(prev => [...prev, trimmedTag]);
    }
    setTagSearch('');
    setShowTagSuggestions(false);
    setCurrentPage(1);
  };

  // Handle Enter key for custom tag input
  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' && tagSearch.trim() && canAddMoreTags) {
      e.preventDefault();
      addTag(tagSearch);
    }
  };

  // Remove a tag
  const removeTag = (tag) => {
    setSelectedStyles(prev => prev.filter(s => s !== tag));
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedStyles([]);
    setTagSearch('');
    setSelectedCategory('');
    setCategorySearch('');
    setSortBy('liked');
    setCurrentPage(1);
  };

  const hasActiveFilters = selectedStyles.length > 0 || selectedCategory;

  // Get the display name for the selected category
  const selectedCategoryName = useMemo(() => {
    if (!selectedCategory) return '';
    const cat = categories.find(c => c._id === selectedCategory);
    return cat ? cat.name : '';
  }, [selectedCategory, categories]);

  // Pagination
  const indexOfLastPost = currentPage * blogsPerPage;
  const indexOfFirstPost = indexOfLastPost - blogsPerPage;
  const currentPosts = filteredAndSortedPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredAndSortedPosts.length / blogsPerPage) || 1;

  // Split posts for expanded layout
  const expandedPost = expandedBlogId ? currentPosts.find(p => p._id === expandedBlogId) : null;
  const otherPosts = expandedBlogId ? currentPosts.filter(p => p._id !== expandedBlogId) : currentPosts;

  const handleNext = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      setExpandedBlogId(null);
      window.scrollTo({ top: 400, behavior: 'smooth' });
    }
  }, [currentPage, totalPages]);

  const handlePrev = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      setExpandedBlogId(null);
      window.scrollTo({ top: 400, behavior: 'smooth' });
    }
  }, [currentPage]);

  const toggleArticle = useCallback((blogId) => {
    if (expandedBlogId === blogId) {
      setExpandedBlogId(null);
    } else {
      setExpandedBlogId(blogId);

      // Record view (once per session per article)
      if (!viewedArticlesRef.current.has(blogId)) {
        viewedArticlesRef.current.add(blogId);
        ApiService.post(`blog/${blogId}/view`).then(res => {
          const newCount = res?.data?.data?.viewCount;
          if (newCount != null) {
            setPosts(prev => prev.map(p =>
              p._id === blogId ? { ...p, viewCount: newCount } : p
            ));
          }
        }).catch(() => {});
      }

      setTimeout(() => {
        const expandedEl = document.getElementById(`expanded-${blogId}`);
        if (expandedEl) {
          expandedEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [expandedBlogId]);

  // Simple like handler using the reusable hook
  const handleLikeClick = useCallback((e, blogId) => {
    e.stopPropagation();
    toggleLike(blogId);
  }, [toggleLike]);

  const handleShare = useCallback((e, blogId) => {
    e.stopPropagation();
    const url = `${window.location.origin}/blog/${blogId}`;
    if (navigator.share) {
      navigator.share({ title: 'Move n Muse Article', url });
    } else {
      navigator.clipboard.writeText(url).then(() => {
        NotificationService.notifySuccess("Link copied to clipboard!");
      });
    }
  }, []);

  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html || '';
    return tmp.textContent || tmp.innerText || '';
  };

  const renderBlogCard = (blog, isPreview = false) => {
    const categoryName = blog.title?.name || 'General';
    const categoryColor = categoryColors[categoryName.toLowerCase()] || categoryColors.default;
    const excerpt = blog.excerpt || stripHtml(blog.message).substring(0, 150) + '...';
    const authorName = blog.publishAs === 'author' ? (blog.author?.fullName || 'Move n Muse Community') : 'Move n Muse Community';
    const articleTitle = blog.articleTitle || blog.title?.name || 'Untitled';
    const featuredImage = blog.featuredImage || '';
    const blogIsLiked = isLiked(blog._id);
    const blogLikeCount = getLikeCount(blog._id);

    return (
      <article
        key={blog._id}
        className={`blog-card ${isPreview ? 'blog-card-preview' : ''}`}
        onClick={() => toggleArticle(blog._id)}
      >
        <div
          className="blog-card-image"
          style={{
            backgroundImage: featuredImage ? `url('${featuredImage}')` : 'none',
            backgroundColor: `${categoryColor}20`
          }}
        >
          <span className="blog-card-category" style={{ backgroundColor: categoryColor }}>
            {categoryName}
          </span>
          {(blog.viewCount > 0) && (
            <span className="blog-card-popularity">
              <i className="bi bi-eye"></i> {blog.viewCount}
            </span>
          )}
        </div>
        <div className="blog-card-content">
          <h3 className="blog-card-title">{articleTitle}</h3>
          <p className="blog-card-excerpt">{excerpt}</p>
          <div className="blog-card-meta">
            <span className="blog-card-author">{authorName}</span>
            <button
              className={`blog-card-like ${blogIsLiked ? 'liked' : ''}`}
              onClick={(e) => handleLikeClick(e, blog._id)}
              title={isLoggedIn() ? 'Like this article' : 'Sign in to like'}
            >
              <i className={`bi ${blogIsLiked ? 'bi-heart-fill' : 'bi-heart'}`}></i>
              <span>{blogLikeCount}</span>
            </button>
          </div>
        </div>
      </article>
    );
  };

  const renderExpandedArticle = (blog) => {
    const categoryName = blog.title?.name || 'General';
    const categoryColor = categoryColors[categoryName.toLowerCase()] || categoryColors.default;
    const displayName = blog.publishAs === 'author' ? (blog.author?.fullName || 'Move n Muse Community') : 'Move n Muse Community';
    const articleTitle = blog.articleTitle || blog.title?.name || 'Untitled';
    const content = blog.message || '';
    const createdAt = new Date(blog.createdAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const tags = blog.tags || blog.hashtags || [];
    const blogIsLiked = isLiked(blog._id);
    const blogLikeCount = getLikeCount(blog._id);

    return (
      <div className="article-expanded" id={`expanded-${blog._id}`}>
        <div className="article-expanded-header">
          <button
            className="article-expanded-close"
            onClick={(e) => { e.stopPropagation(); setExpandedBlogId(null); }}
          >
            <i className="bi bi-x"></i>
          </button>
          <span className="article-expanded-category" style={{ backgroundColor: categoryColor }}>
            {categoryName}
          </span>
          <h1 className="article-expanded-title">{articleTitle}</h1>
          <div className="article-expanded-meta">
            <span><i className="bi bi-person"></i>{displayName}</span>
            <span><i className="bi bi-calendar3"></i>{createdAt}</span>
            <span><i className="bi bi-eye"></i>{blog.viewCount || 0} views</span>
          </div>
        </div>
        <div className="article-expanded-body">
          <div
            className="article-expanded-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
        <div className="article-expanded-signature">
          ~ {displayName}
        </div>
        <div className="article-expanded-footer">
          <div className="article-expanded-tags">
            {tags.map((tag, index) => (
              <span key={index} className="article-expanded-tag">{tag}</span>
            ))}
          </div>
          <div className="article-expanded-actions">
            <button
              className="share-btn"
              onClick={(e) => handleShare(e, blog._id)}
            >
              <i className="bi bi-share"></i> Share
            </button>
            <div className="like-btn-wrapper">
              <button
                className={`article-like-btn ${blogIsLiked ? 'liked' : ''}`}
                onClick={(e) => handleLikeClick(e, blog._id)}
              >
                <i className={`bi ${blogIsLiked ? 'bi-heart-fill' : 'bi-heart'}`}></i>
                <span className="like-count">{blogLikeCount}</span>
              </button>
              {!isLoggedIn() && (
                <span className="like-tooltip">Sign in to save to favorites</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="blog-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <h1 className="hero-title">Welcome to Move n Muse <span className="highlight-yellow">Blog!</span></h1>
          <p className="hero-subtitle">Where dancers share their bits of wisdom and encouragement.</p>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <p className="values-intro">All of the essays are aligned with Move n Muse community values:</p>
          <ValueTiles />
        </div>
      </section>

      {/* Filter Section */}
      <section className="filter-section">
        <div className="container">
          {/* Filter Bar */}
          <div className="filter-bar">
            <span className="filter-count">
              {filteredAndSortedPosts.length} Article{filteredAndSortedPosts.length !== 1 ? 's' : ''}
            </span>

            <div className="filter-controls">
              {/* Sort Dropdown */}
              <select
                className="filter-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="liked">Most Liked</option>
                <option value="viewed">Most Viewed</option>
                <option value="recent">Most Recent</option>
              </select>

              {/* Category Filter */}
              <div className="filter-input-wrapper">
                <input
                  type="text"
                  className="filter-select filter-input filter-input-category"
                  placeholder={selectedCategory ? selectedCategoryName : "Category"}
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  onFocus={() => setShowCategorySuggestions(true)}
                  onBlur={() => setTimeout(() => setShowCategorySuggestions(false), 200)}
                />
                {selectedCategory && (
                  <button
                    className="filter-input-clear"
                    onClick={() => { setSelectedCategory(''); setCategorySearch(''); setCurrentPage(1); }}
                  >
                    ×
                  </button>
                )}
                {showCategorySuggestions && categorySuggestions.length > 0 && (
                  <div className="filter-dropdown">
                    {categorySuggestions.map((cat) => (
                      <button
                        key={cat._id}
                        className={`filter-dropdown-item ${selectedCategory === cat._id ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedCategory(cat._id);
                          setCategorySearch('');
                          setShowCategorySuggestions(false);
                          setCurrentPage(1);
                        }}
                      >
                        {cat.name}
                        <span className="filter-dropdown-count">{cat.blogCount || 0}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Filter Input */}
              <div className="filter-input-wrapper">
                <input
                  type="text"
                  className="filter-select filter-input"
                  placeholder={canAddMoreTags ? "+ Add filter" : "Max 7 tags"}
                  value={tagSearch}
                  onChange={(e) => setTagSearch(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onFocus={() => canAddMoreTags && setShowTagSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                  disabled={!canAddMoreTags}
                />
                {showTagSuggestions && tagSuggestions.length > 0 && (
                  <div className="filter-dropdown">
                    {tagSuggestions.slice(0, 8).map((tag) => (
                      <button
                        key={tag}
                        className="filter-dropdown-item"
                        onClick={() => addTag(tag)}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button className="clear-filters-btn" onClick={clearFilters}>
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Selected Tags Row - Below Separator */}
          {(selectedStyles.length > 0 || selectedCategory) && (
            <div className="blog-tags-row">
              {selectedCategory && (
                <span className="filter-tag filter-tag-category">
                  {selectedCategoryName}
                  <button className="filter-tag-remove" onClick={() => { setSelectedCategory(''); setCurrentPage(1); }}>×</button>
                </span>
              )}
              {selectedStyles.map((tag) => (
                <span key={tag} className="filter-tag">
                  {tag}
                  <button className="filter-tag-remove" onClick={() => removeTag(tag)}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Blog Grid */}
      <section className="container">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        ) : error ? (
          <div className="empty-state">
            <i className="bi bi-exclamation-circle"></i>
            <h3>Oops!</h3>
            <p>{error}</p>
          </div>
        ) : filteredAndSortedPosts.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-journal-text"></i>
            <h3>{hasActiveFilters ? 'No matching articles' : 'No articles yet'}</h3>
            <p>
              {hasActiveFilters
                ? 'Try adjusting your filters to find more articles.'
                : 'Be the first to share your dance wisdom with the community!'}
            </p>
            {hasActiveFilters ? (
              <button className="load-more-btn" onClick={clearFilters}>Clear Filters</button>
            ) : (
              <Link to="/catalogue" className="load-more-btn">Write an Article</Link>
            )}
          </div>
        ) : expandedPost ? (
          <>
            {/* Expanded layout: article on top, previews below */}
            {renderExpandedArticle(expandedPost)}

            {otherPosts.length > 0 && (
              <div className="blog-preview-row">
                {otherPosts.map(blog => renderBlogCard(blog, true))}
              </div>
            )}

            {/* Navigation Arrows */}
            <div className="nav-arrows">
              <button
                className="nav-arrow-btn"
                onClick={handlePrev}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <img src={back} alt="Previous" />
              </button>
              <button
                className="nav-arrow-btn"
                onClick={handleNext}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
              >
                <img src={forward} alt="Next" />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="blog-grid">
              {currentPosts.map(blog => renderBlogCard(blog))}
            </div>

            {/* Navigation Arrows */}
            <div className="nav-arrows">
              <button
                className="nav-arrow-btn"
                onClick={handlePrev}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <img src={back} alt="Previous" />
              </button>
              <button
                className="nav-arrow-btn"
                onClick={handleNext}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
              >
                <img src={forward} alt="Next" />
              </button>
            </div>
          </>
        )}
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-icon">
            <img src={blogShareIcon} alt="Share your essay" />
          </div>
          <p className="cta-text">
            Would like to share your own dance musing?{' '}
            <Link to="/catalogue" className="cta-link">Click here to pick an existing or create a new topic!</Link>
          </p>
        </div>
      </section>

      {/* Floating Write Icon */}
      <div className="write-icon-container">
        <Link to="/catalogue" className="write-icon" title="Write an article">
          <span className="write-icon-tooltip">Share your musing</span>
          <img src={blogShareIcon} alt="Write an article" />
        </Link>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="login-modal-backdrop show" onClick={() => setShowLoginModal(false)}>
          <div className="login-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="close-btn"
              onClick={() => setShowLoginModal(false)}
            >
              <i className="bi bi-x-lg"></i>
            </button>
            <i className="bi bi-heart-fill"></i>
            <h3>Save to Favorites</h3>
            <p>Sign in to save articles to your favorites and get personalized recommendations.</p>

            <Link to="/login" className="login-btn">
              <i className="bi bi-box-arrow-in-right"></i> Sign In
            </Link>

            <div className="login-divider"><span>or</span></div>

            <p className="text-muted small mb-2">
              Don't have an account?{' '}
              <Link to="/catalogue" style={{ color: 'var(--purple-accent)' }}>Sign up to write</Link>
            </p>

            <button
              className="skip-link"
              onClick={() => setShowLoginModal(false)}
            >
              Continue browsing without signing in
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPage;
